import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import DealForm from '../../components/deals/DealForm';
import { Card, Button, Space, message, Spin } from 'antd';
import { PlusOutlined, ArrowRightOutlined, DollarOutlined } from '@ant-design/icons';
import { dealsApi, ValidationError, leadsApi } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import moment from 'moment-jalaali';
import { LEAD_STEP_CONVERTED_TO_DEAL } from '../../utils/leadStepUtils';
import {
  parseDealCreateResponse,
  mapDealCreateToInitialValues,
  resolveDealCreatePresetLead,
  resolveDealCreatePresetClient,
} from '../../utils/dealCreateFormUtils';

const persianToLatinNumbers = (str) => {
  if (!str) return str;
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let result = String(str);
  for (let i = 0; i < 10; i++) {
    result = result.replace(new RegExp(persianDigits[i], 'g'), i);
    result = result.replace(new RegExp(arabicDigits[i], 'g'), i);
  }
  return result;
};

const buildDealInfoPayload = (values, closeDate, user) => ({
  name: values.name || '',
  value: values.value ? Number(values.value) : 0,
  close_date: closeDate,
  deal_owner: values.deal_owner ? Number(values.deal_owner) : (user?.id ? Number(user.id) : null),
  deal_agent_id: null,
  pipeline: values.pipeline ? Number(values.pipeline) : null,
  stage_id: values.stage_id ? Number(values.stage_id) : null,
  category_id: values.category_id ? Number(values.category_id) : null,
  tags: Array.isArray(values.tags) ? values.tags.map(Number) : [],
  source_id: values.source_id != null && values.source_id !== '' ? Number(values.source_id) : null,
  priority: values.priority != null && values.priority !== '' ? Number(values.priority) : null,
  product_id: Array.isArray(values.product_id) ? values.product_id.map(Number) : [],
  success_rate: values.success_rate != null && values.success_rate !== '' ? Number(values.success_rate) : null,
});

const AddDeal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [createBootstrap, setCreateBootstrap] = useState(null);

  const leadId = searchParams.get('lead_id');
  const clientId = searchParams.get('client_id');
  const stageIdFromUrl = searchParams.get('stage_id');
  const pipelineIdFromState = location.state?.pipelineId;

  const createData = useMemo(
    () => (createBootstrap ? parseDealCreateResponse(createBootstrap) : null),
    [createBootstrap],
  );

  const isFromLead = Boolean(leadId || createData?.lead);
  const isFromClient = Boolean(clientId || createData?.client);

  const returnTo = useMemo(() => {
    if (location.state?.returnTo) return location.state.returnTo;
    if (isFromLead && leadId) return `/leads/${leadId}`;
    if (isFromClient && clientId) return `/clients/${clientId}?tab=deals`;
    return '/leads/deals';
  }, [location.state?.returnTo, isFromLead, isFromClient, leadId, clientId]);

  useEffect(() => {
    let cancelled = false;
    const loadCreateForm = async () => {
      setBootstrapLoading(true);
      try {
        const res = await dealsApi.getCreateForm({
          leadId: leadId || undefined,
          clientId: clientId || undefined,
        });
        if (!cancelled) setCreateBootstrap(res);
      } catch (error) {
        console.error('خطا در بارگذاری فرم ایجاد معامله:', error);
        if (!cancelled) message.error('خطا در دریافت اطلاعات فرم معامله');
      } finally {
        if (!cancelled) setBootstrapLoading(false);
      }
    };
    loadCreateForm();
    return () => {
      cancelled = true;
    };
  }, [leadId, clientId]);

  const initialValues = useMemo(() => {
    if (!createData) {
      const partial = {};
      if (stageIdFromUrl) partial.stage_id = Number(stageIdFromUrl);
      if (pipelineIdFromState) partial.pipeline = Number(pipelineIdFromState);
      return Object.keys(partial).length ? partial : null;
    }
    return mapDealCreateToInitialValues(createData, {
      stageIdFromUrl,
      userId: user?.id,
      pipelineIdFromFilter: pipelineIdFromState,
      leadNoteFallback: location.state?.leadNote,
    });
  }, [createData, stageIdFromUrl, pipelineIdFromState, user?.id, location.state?.leadNote]);

  const presetLeadOption = useMemo(
    () => resolveDealCreatePresetLead(createData),
    [createData],
  );

  const presetClientOption = useMemo(
    () => resolveDealCreatePresetClient(createData),
    [createData],
  );

  const buildPayload = (values) => {
    let closeDate = null;
    if (values.close_date) {
      if (values.close_date && typeof values.close_date.format === 'function') {
        closeDate = persianToLatinNumbers(values.close_date.format('YYYY/MM/DD'));
      } else if (moment.isMoment(values.close_date)) {
        closeDate = persianToLatinNumbers(values.close_date.format('jYYYY/jMM/jDD'));
      } else if (typeof values.close_date === 'string') {
        closeDate = persianToLatinNumbers(values.close_date);
      }
    }

    const dealInfo = buildDealInfoPayload(values, closeDate, user);
    const note = values.note != null && String(values.note).trim() !== '' ? String(values.note).trim() : null;
    const selectedContactId = values.lead_contact ? Number(values.lead_contact) : null;

    // حالت ۱: تبدیل سرنخ به معامله — فقط lead_id
    if (isFromLead && !isFromClient) {
      return {
        lead_id: selectedContactId,
        ...dealInfo,
        note,
      };
    }

    // حالت ۲: معامله برای مشتری موجود (از پروفایل مشتری یا انتخاب از لیست مشتریان) — فقط client_id
    const usingExistingClient = isFromClient || Boolean(values.has_lead_contact);
    if (usingExistingClient) {
      return {
        client_id: selectedContactId,
        ...dealInfo,
        note,
      };
    }

    // حالت ۳: ایجاد مشتری جدید — فقط اطلاعات مشتری (بدون lead_id/client_id)
    return {
      client_name: values.client_name || '',
      mobile: values.mobile || '',
      email: values.email || '',
      salutation:
        values.salutation != null && values.salutation !== '' ? Number(values.salutation) : null,
      client_category_id:
        values.client_category_id != null && values.client_category_id !== ''
          ? Number(values.client_category_id)
          : null,
      added_by: values.added_by ? Number(values.added_by) : (user?.id ? Number(user.id) : null),
      ...dealInfo,
      note,
    };
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const payload = buildPayload(values);
      const res = await dealsApi.create(payload);
      message.success('معامله با موفقیت ثبت شد');

      if (isFromLead && !isFromClient && values.lead_contact) {
        try {
          await leadsApi.changeStep(values.lead_contact, LEAD_STEP_CONVERTED_TO_DEAL);
        } catch (stepError) {
          console.error('Error updating lead step after deal creation:', stepError);
        }
      }

      const body = res?.data || res || {};
      const newId = body?.deal?.id || body?.deal_id || body?.id;
      if (newId) {
        navigate(`/deals/${newId}`, {
          state: isFromLead && leadId ? { returnTo: `/leads/${leadId}` } : undefined,
        });
      } else {
        navigate(returnTo);
      }
    } catch (error) {
      console.error('خطا در ذخیره:', error);
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        throw error;
      }
      message.error(error?.message || 'خطا در ذخیره معامله');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <DollarOutlined className="text-green-600 dark:text-green-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">افزودن معامله جدید</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">اطلاعات معامله را تکمیل و ثبت کنید</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate(returnTo)} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
              <Button type="primary" icon={<PlusOutlined />} disabled>
                راهنما
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {bootstrapLoading ? (
              <div className="flex justify-center py-16">
                <Spin size="large" tip="در حال بارگذاری فرم..." />
              </div>
            ) : (
              <DealForm
                initialValues={initialValues}
                createFormBootstrap={createBootstrap}
                presetLeadOption={presetLeadOption}
                presetClientOption={presetClientOption}
                lockLeadContact={isFromLead}
                lockClientContact={isFromClient}
                existingContactSource="client"
                onSave={handleSave}
                onCancel={() => navigate(returnTo)}
                loading={loading}
                isEdit={false}
              />
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddDeal;
